<template>
  <div class="reports-panel">
    <v-layout row wrap align-center mb-3>
      <v-flex>
        <span class="subheading">Manage report subscriptions for this user</span>
      </v-flex>
      <v-flex shrink>
        <v-btn small outline color="primary" @click="showResendModal = true" :disabled="!userAccounts.length || !userReports.length" class="mr-2">
          <v-icon small left>fa-refresh</v-icon>
          Resend Reports
        </v-btn>
        <v-btn small color="primary" @click="showAddModal = true" :disabled="!userAccounts.length">
          <v-icon small left>fa-plus</v-icon>
          Add Reports
        </v-btn>
      </v-flex>
    </v-layout>

    <v-alert v-if="!userAccounts.length" type="info" :value="true">
      User has no linked accounts. Link an account first to manage reports.
    </v-alert>

    <v-data-table
      v-else
      :headers="tableHeaders"
      :items="reportTableData"
      :loading="isLoading"
      hide-actions
      class="elevation-1"
    >
      <template v-slot:items="props">
        <td>{{ props.item.account_name }}</td>
        <td>{{ props.item.report_description }}</td>
        <td class="text-xs-right">
          <v-btn small flat color="error" @click="removeReport(props.item)">
            <v-icon small>fa-trash</v-icon>
          </v-btn>
        </td>
      </template>
      <template v-slot:no-data>
        <v-alert :value="true" type="info">
          No report subscriptions for this user
        </v-alert>
      </template>
    </v-data-table>

    <!-- Add Reports Modal -->
    <v-dialog v-model="showAddModal" max-width="700px" persistent>
      <v-card>
        <v-card-title class="headline primary white--text">
          Add Reports
          <v-spacer></v-spacer>
          <v-btn icon dark @click="closeAddModal">
            <v-icon>fa-times</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-4">
          <!-- Section 1: Account Selection -->
          <v-select
            v-model="selectedAccount"
            :items="userAccounts"
            item-text="account_name"
            item-value="account_id"
            label="Select Account"
            prepend-icon="fa-building"
            box
          ></v-select>

          <!-- Section 2: Preset Dropdown -->
          <v-select
            v-model="selectedPreset"
            :items="reportPresets"
            item-text="name"
            item-value="key"
            label="Quick Select Preset"
            prepend-icon="fa-bookmark"
            box
            clearable
            @change="onPresetChange"
          ></v-select>

          <!-- Preset Reference Info -->
          <v-expansion-panel class="mb-3">
            <v-expansion-panel-content>
              <template v-slot:header>
                <div class="subheading">Preset Reference Guide</div>
              </template>
              <v-card>
                <v-card-text class="preset-reference">
                  <div class="preset-section">
                    <strong>Standard Reports</strong>
                    <ul>
                      <li>card_settled_transactions</li>
                      <li>ach_settled_transactions</li>
                      <li>card_recurring_failure</li>
                      <li>ach_reject</li>
                      <li>chargeback</li>
                    </ul>
                  </div>

                  <div class="preset-section">
                    <strong>BlueYonder Reports</strong>
                    <ul>
                      <li>card_settled_transactions</li>
                      <li>ach_settled_transactions</li>
                      <li>card_recurring_failure</li>
                      <li>ach_reject</li>
                      <li>chargeback</li>
                      <li>transactions</li>
                    </ul>
                  </div>

                  <div class="preset-section">
                    <strong>Reports Based on Created Date</strong>
                    <div class="caption grey--text mb-1">(Sent regardless of cutoff time)</div>
                    <ul>
                      <li>card_transactions</li>
                      <li>ach_transactions</li>
                      <li>ach_reject</li>
                      <li>chargeback</li>
                      <li>card_recurring_failure</li>
                      <li>monthly_volume</li>
                      <li>monthly_recurring</li>
                      <li>monthly_ach_rejects</li>
                      <li>atmone_fee_summary</li>
                    </ul>
                  </div>

                  <div class="preset-section">
                    <strong>Reports Based on Authorized Date</strong>
                    <ul>
                      <li>ach_authorized_transactions</li>
                      <li>card_authorized_transactions</li>
                    </ul>
                  </div>

                  <div class="preset-section">
                    <strong>Reports Based on Settled Date</strong>
                    <ul>
                      <li>card_settled_transactions</li>
                      <li>ach_settled_transactions</li>
                      <li>ach_pending_settled_transactions</li>
                      <li>atmone_settled_transactions</li>
                      <li>fpg_card_settled_transactions</li>
                      <li>atmone_agent_settled_transactions</li>
                      <li>telagility_settled_transactions</li>
                      <li>card_monthly_settled_transactions</li>
                      <li>ach_monthly_settled_transactions</li>
                      <li>card_settled_transactions_monthly</li>
                      <li>ach_settled_transactions_monthly</li>
                    </ul>
                  </div>

                  <div class="preset-section">
                    <strong>Dubrow Reporting</strong>
                    <div class="caption grey--text mb-1">(For merchants processing after FAP cutoff time)</div>
                    <ul>
                      <li>card_transactions</li>
                      <li>ach_reject</li>
                      <li>card_recurring_failure</li>
                      <li>ach_transactions</li>
                      <li>chargeback</li>
                    </ul>
                  </div>

                  <div class="preset-section">
                    <strong>ACH Reports</strong>
                    <ul>
                      <li>ach_settled_transactions</li>
                      <li>ach_reject</li>
                      <li>ach_pending_settled_transactions</li>
                      <li>ach_transactions</li>
                      <li>ach_authorized_transactions</li>
                    </ul>
                  </div>

                  <div class="preset-section">
                    <strong>Card Reports</strong>
                    <ul>
                      <li>card_settled_transactions</li>
                      <li>card_recurring_failure</li>
                      <li>chargeback</li>
                      <li>card_transactions</li>
                      <li>card_authorized_transactions</li>
                    </ul>
                  </div>

                  <div class="preset-section">
                    <strong>All Reports</strong>
                    <div class="caption grey--text mb-1">(Selects all available reports)</div>
                  </div>
                </v-card-text>
              </v-card>
            </v-expansion-panel-content>
          </v-expansion-panel>

          <v-divider class="mb-3"></v-divider>

          <!-- Section 3: All Reports as Checkboxes -->
          <div class="subheading mb-2">Select Individual Reports</div>
          <div class="reports-checkbox-grid">
            <v-checkbox
              v-for="report in availableReports"
              :key="report.name"
              v-model="selectedReports"
              :label="report.description"
              :value="report.name"
              color="primary"
              hide-details
              class="mt-1 mb-1"
            ></v-checkbox>
          </div>

          <div class="mt-2 caption grey--text">
            {{ selectedReports.length }} report(s) selected
          </div>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-btn flat @click="closeAddModal">Cancel</v-btn>
          <v-btn flat @click="resetCheckboxes">Reset</v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            :disabled="!selectedAccount || !selectedReports.length || isProcessing"
            :loading="isProcessing"
            @click="addReports"
          >
            Add Reports
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Resend Reports Modal -->
    <v-dialog v-model="showResendModal" max-width="500px" persistent>
      <v-card>
        <v-card-title class="headline primary white--text">
          Resend Reports
          <v-spacer></v-spacer>
          <v-btn icon dark @click="closeResendModal">
            <v-icon>fa-times</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-4">
          <v-select
            v-model="resendAccount"
            :items="userAccounts"
            item-text="account_name"
            item-value="account_id"
            label="Select Account"
            prepend-icon="fa-building"
            box
          ></v-select>

          <v-menu
            v-model="resendDateMenu"
            :close-on-content-click="false"
            lazy
            transition="scale-transition"
            offset-y
            full-width
            min-width="290px"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                v-model="resendDateDisplay"
                label="Select Date(s)"
                prepend-icon="fa-calendar"
                readonly
                box
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="resendDates"
              multiple
            ></v-date-picker>
          </v-menu>
          <div class="caption grey--text mt-1">
            Click dates to select. Click again to deselect.
          </div>

          <v-radio-group v-model="resendScope" class="mt-2">
            <v-radio label="This User Only" value="user" color="primary"></v-radio>
            <v-radio label="All Users for Account" value="account" color="primary"></v-radio>
          </v-radio-group>

          <v-alert v-if="resendAccount && resendScope === 'user'" type="info" :value="true" class="mt-2">
            <strong>Reports to be sent:</strong>
            <ul class="mt-1 mb-0">
              <li v-for="report in getResendReports()" :key="report">
                {{ getReportDescription(report) }}
              </li>
            </ul>
            <div v-if="!getResendReports().length" class="caption">
              No reports configured for this account
            </div>
          </v-alert>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-btn flat @click="closeResendModal">Cancel</v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            :disabled="!resendAccount || !hasValidResendDates || isResending"
            :loading="isResending"
            @click="resendReports"
          >
            Resend Reports
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Bus from '@/bus'
import ApiMixin from '@/mixins/api'

export default {
  name: 'ReportsPanel',
  mixins: [ApiMixin],

  props: {
    userId: {
      type: [Number, String],
      required: true
    },
    userAccounts: {
      type: Array,
      default: () => []
    }
  },

  data () {
    return {
      isLoading: false,
      isProcessing: false,
      isResending: false,
      showAddModal: false,
      showResendModal: false,
      selectedAccount: null,
      selectedPreset: null,
      selectedReports: [],
      resendAccount: null,
      resendDates: [],
      resendDateMenu: false,
      resendScope: 'user',
      availableReports: [],
      userReports: [],
      reportsByAccount: {},
      tableHeaders: [
        { text: 'Account', value: 'account_name', sortable: true },
        { text: 'Report', value: 'report_description', sortable: true },
        { text: 'Actions', value: 'actions', sortable: false, align: 'right' }
      ],
      reportPresets: [
        { key: 'standard', name: 'Standard Reports' },
        { key: 'blueyonder', name: 'BlueYonder Reports' },
        { key: 'created_date', name: 'Reports Based on Created Date', subtitle: 'Sent regardless of cutoff time' },
        { key: 'authorized_date', name: 'Reports Based on Authorized Date' },
        { key: 'settled_date', name: 'Reports Based on Settled Date' },
        { key: 'dubrow', name: 'Dubrow Reporting', subtitle: 'For merchants processing after FAP cutoff time' },
        { key: 'ach', name: 'ACH Reports' },
        { key: 'card', name: 'Card Reports' },
        { key: 'all', name: 'All Reports' }
      ],
      presetDefinitions: {
        standard: [
          'card_settled_transactions',
          'ach_settled_transactions',
          'card_recurring_failure',
          'ach_reject',
          'chargeback'
        ],
        blueyonder: [
          'card_settled_transactions',
          'ach_settled_transactions',
          'card_recurring_failure',
          'ach_reject',
          'chargeback',
          'transactions'
        ],
        created_date: [
          'card_transactions',
          'ach_transactions',
          'ach_reject',
          'chargeback',
          'card_recurring_failure',
          'monthly_volume',
          'monthly_recurring',
          'monthly_ach_rejects',
          'atmone_fee_summary'
        ],
        authorized_date: [
          'ach_authorized_transactions',
          'card_authorized_transactions'
        ],
        settled_date: [
          'card_settled_transactions',
          'ach_settled_transactions',
          'ach_pending_settled_transactions',
          'atmone_settled_transactions',
          'fpg_card_settled_transactions',
          'atmone_agent_settled_transactions',
          'telagility_settled_transactions',
          'card_monthly_settled_transactions',
          'ach_monthly_settled_transactions',
          'card_settled_transactions_monthly',
          'ach_settled_transactions_monthly'
        ],
        dubrow: [
          'card_transactions',
          'ach_reject',
          'card_recurring_failure',
          'ach_transactions',
          'chargeback'
        ],
        ach: [
          'ach_settled_transactions',
          'ach_reject',
          'ach_pending_settled_transactions',
          'ach_transactions',
          'ach_authorized_transactions'
        ],
        card: [
          'card_settled_transactions',
          'card_recurring_failure',
          'chargeback',
          'card_transactions',
          'card_authorized_transactions'
        ],
        all: [] // Will be populated from availableReports
      }
    }
  },

  computed: {
    resendDateDisplay () {
      if (!this.resendDates.length) return ''
      if (this.resendDates.length === 1) return this.resendDates[0]
      return `${this.resendDates.length} dates selected`
    },

    hasValidResendDates () {
      return this.resendDates.length > 0
    },

    reportTableData () {
      let data = []
      for (let report of this.userReports) {
        let account = this.userAccounts.find(a => a.account_id === report.account_id)
        let reportDef = this.availableReports.find(r => r.name === report.report)
        data.push({
          account_id: report.account_id,
          account_name: account ? account.account_name : `Account ${report.account_id}`,
          report_name: report.report,
          report_description: reportDef ? reportDef.description : report.report
        })
      }
      return data
    }
  },

  watch: {
    userId: {
      immediate: true,
      handler () {
        if (this.userId) {
          this.loadData()
        }
      }
    }
  },

  methods: {
    async loadData () {
      this.isLoading = true
      try {
        let [availableResult, userResult] = await Promise.all([
          this.api({ method: 'get', url: '/users/available-reports' }),
          this.api({ method: 'get', url: `/users/${this.userId}/reports` })
        ])

        this.availableReports = availableResult.data.reports
        this.userReports = userResult.data.reports

        // Populate 'all' preset with all available reports
        this.presetDefinitions.all = this.availableReports.map(r => r.name)

        // Build lookup by account
        this.reportsByAccount = {}
        for (let report of this.userReports) {
          if (!this.reportsByAccount[report.account_id]) {
            this.reportsByAccount[report.account_id] = []
          }
          this.reportsByAccount[report.account_id].push(report.report)
        }
      } catch (error) {
        console.error(error)
        Bus.$emit('openModal', {
          status: 'error',
          message: 'Error loading reports'
        })
      } finally {
        this.isLoading = false
      }
    },

    getPresetReports (presetKey) {
      return this.presetDefinitions[presetKey] || []
    },

    getReportDescription (reportName) {
      let report = this.availableReports.find(r => r.name === reportName)
      return report ? report.description : reportName
    },

    async removeReport (item) {
      let currentReports = this.reportsByAccount[item.account_id] || []
      let newReports = currentReports.filter(r => r !== item.report_name)

      try {
        await this.api({
          method: 'put',
          url: `/users/${this.userId}/reports`,
          data: {
            account_id: item.account_id,
            reports: newReports
          }
        })

        this.reportsByAccount[item.account_id] = newReports
        this.userReports = this.userReports.filter(
          r => !(r.account_id === item.account_id && r.report === item.report_name)
        )

        Bus.$emit('openModal', {
          status: 'success',
          message: 'Report removed successfully'
        })
      } catch (error) {
        Bus.$emit('openModal', {
          status: 'error',
          message: 'Error removing report'
        })
      }
    },

    async addReports () {
      if (!this.selectedAccount || !this.selectedReports.length) {
        return
      }

      this.isProcessing = true
      try {
        let currentReports = this.reportsByAccount[this.selectedAccount] || []

        // Merge current reports with new reports (avoid duplicates)
        let newReports = [...new Set([...currentReports, ...this.selectedReports])]

        await this.api({
          method: 'put',
          url: `/users/${this.userId}/reports`,
          data: {
            account_id: this.selectedAccount,
            reports: newReports
          }
        })

        Bus.$emit('openModal', {
          status: 'success',
          message: 'Reports added successfully'
        })

        this.closeAddModal()
        this.loadData()
      } catch (error) {
        Bus.$emit('openModal', {
          status: 'error',
          message: 'Error adding reports'
        })
      } finally {
        this.isProcessing = false
      }
    },

    closeAddModal () {
      this.showAddModal = false
      this.selectedAccount = null
      this.selectedPreset = null
      this.selectedReports = []
    },

    applyPreset (presetKey) {
      let presetReports = this.getPresetReports(presetKey)
      // Add preset reports to selected (avoid duplicates)
      this.selectedReports = [...new Set([...this.selectedReports, ...presetReports])]
    },

    onPresetChange (presetKey) {
      if (presetKey) {
        this.applyPreset(presetKey)
      }
    },

    isPresetFullySelected (presetKey) {
      let presetReports = this.getPresetReports(presetKey)
      if (!presetReports.length) return false
      return presetReports.every(report => this.selectedReports.includes(report))
    },

    togglePreset (presetKey, checked) {
      let presetReports = this.getPresetReports(presetKey)
      if (checked) {
        // Add all preset reports
        this.selectedReports = [...new Set([...this.selectedReports, ...presetReports])]
      } else {
        // Remove all preset reports
        this.selectedReports = this.selectedReports.filter(r => !presetReports.includes(r))
      }
    },

    resetCheckboxes () {
      this.selectedReports = []
    },

    closeResendModal () {
      this.showResendModal = false
      this.resendAccount = null
      this.resendDates = []
      this.resendScope = 'user'
    },

    getResendReports () {
      if (!this.resendAccount) return []
      const nonResendable = ['chargeback', 'card_recurring_failure', 'ach_reject']
      return (this.reportsByAccount[this.resendAccount] || []).filter(r => !nonResendable.includes(r))
    },

    async resendReports () {
      if (!this.resendAccount || !this.resendDates.length) {
        return
      }

      this.isResending = true
      try {
        await this.api({
          method: 'post',
          url: `/users/${this.userId}/reports/resend`,
          data: {
            account_id: this.resendAccount,
            scope: this.resendScope,
            dates: this.resendDates
          }
        })

        Bus.$emit('openModal', {
          status: 'success',
          message: 'Reports have been queued for resending'
        })

        this.closeResendModal()
      } catch (error) {
        Bus.$emit('openModal', {
          status: 'error',
          message: (error.response && error.response.data && error.response.data.error) || 'Error resending reports'
        })
      } finally {
        this.isResending = false
      }
    }
  }
}
</script>

<style scoped>
.reports-panel {
  padding: 8px;
}

.preset-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.preset-checkboxes {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 4px;
}

.reports-checkbox-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 4px;
  max-height: 300px;
  overflow-y: auto;
  padding: 8px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}

.preset-reference {
  columns: 2;
  column-gap: 24px;
}

.preset-section {
  break-inside: avoid;
  margin-bottom: 16px;
}

.preset-section ul {
  margin: 4px 0 0 0;
  padding-left: 20px;
}

.preset-section li {
  font-size: 12px;
  font-family: monospace;
}
</style>



// WEBPACK FOOTER //
// src/components/manage/user/ReportsPanel.vue